import { useState, useEffect, useCallback } from "react";
import {
  Plus, Trash2, Tag, CheckCircle, AlertCircle, X,
  RefreshCw, ToggleLeft, ToggleRight, Calendar, Hash,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

const LEGE_FORM = { code: "", percentage: "", max_gebruik: "", vervaldatum: "" };

export default function KortingcodesTab({ token }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(LEGE_FORM);
  const [opslaan, setOpslaan] = useState(false);
  const [fout, setFout] = useState("");
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const showToast = (msg, type = "success") => setToast({ msg, type });
  const auth = { Authorization: `Bearer ${token}` };

  const laad = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/kortingscodes`, { headers: auth });
      const d = await r.json();
      setCodes(Array.isArray(d) ? d : []);
    } catch { showToast("Laden mislukt", "error"); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { laad(); }, [laad]);

  const handleAanmaken = async (e) => {
    e.preventDefault();
    setFout("");
    if (!form.code.trim()) { setFout("Voer een code in"); return; }
    if (!form.percentage || parseInt(form.percentage) < 1 || parseInt(form.percentage) > 100) {
      setFout("Percentage moet tussen 1 en 100 zijn"); return;
    }
    setOpslaan(true);
    try {
      const r = await fetch(`${API}/api/admin/kortingscodes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth },
        body: JSON.stringify({
          code: form.code.trim().toUpperCase(),
          percentage: parseInt(form.percentage),
          max_gebruik: form.max_gebruik ? parseInt(form.max_gebruik) : null,
          vervaldatum: form.vervaldatum || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Aanmaken mislukt");
      setForm(LEGE_FORM);
      showToast(`Code '${d.code}' aangemaakt!`);
      laad();
    } catch (err) { setFout(err.message); }
    finally { setOpslaan(false); }
  };

  const handleToggle = async (id, huidigActief) => {
    try {
      const r = await fetch(`${API}/api/admin/kortingscodes/${id}/toggle`, {
        method: "PUT", headers: auth,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setCodes(cs => cs.map(c => c.id === id ? { ...c, actief: d.actief } : c));
      showToast(d.actief ? "Code geactiveerd" : "Code gedeactiveerd");
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleVerwijder = async (id) => {
    try {
      const r = await fetch(`${API}/api/admin/kortingscodes/${id}`, {
        method: "DELETE", headers: auth,
      });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setDeleteConfirm(null);
      showToast("Code verwijderd");
      laad();
    } catch (err) { showToast(err.message, "error"); }
  };

  const isVerlopen = (datum) => {
    if (!datum) return false;
    return new Date(datum) < new Date();
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Promoties</p>
        <h2 className="text-xl font-bold text-white">Kortingscodes</h2>
        <p className="text-zinc-500 text-sm mt-0.5">Maak kortingscodes aan voor B2C en B2B checkout.</p>
      </div>

      {/* Aanmaken form */}
      <form onSubmit={handleAanmaken} className="bg-[#111] border border-white/8 rounded-2xl p-6">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2">
          <Tag size={15} className="text-[#C9A84C]" /> Nieuwe kortingscode
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-1.5">Code</label>
            <input
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="ZOMER25"
              data-testid="kortingscode-code-input"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white font-mono font-bold text-sm focus:outline-none focus:border-[#C9A84C]/50 uppercase"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-1.5">Korting (%)</label>
            <input
              type="number" min="1" max="100"
              value={form.percentage}
              onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))}
              placeholder="25"
              data-testid="kortingscode-percentage-input"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-1.5">Max gebruik <span className="normal-case text-zinc-600">(leeg = onbeperkt)</span></label>
            <input
              type="number" min="1"
              value={form.max_gebruik}
              onChange={e => setForm(f => ({ ...f, max_gebruik: e.target.value }))}
              placeholder="100"
              data-testid="kortingscode-max-gebruik-input"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest block mb-1.5">Vervaldatum <span className="normal-case text-zinc-600">(leeg = nooit)</span></label>
            <input
              type="date"
              value={form.vervaldatum}
              onChange={e => setForm(f => ({ ...f, vervaldatum: e.target.value }))}
              data-testid="kortingscode-vervaldatum-input"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
            />
          </div>
        </div>
        {fout && <p className="text-red-400 text-sm mb-3">{fout}</p>}
        <button
          type="submit"
          disabled={opslaan}
          data-testid="kortingscode-aanmaken-btn"
          className="flex items-center gap-2 px-6 py-3 text-black font-bold text-sm rounded-xl disabled:opacity-50"
          style={{ background: "#C9A84C" }}
        >
          <Plus size={14} /> {opslaan ? "Aanmaken..." : "Code aanmaken"}
        </button>
      </form>

      {/* Overzicht */}
      <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-white font-bold">Actieve codes</h3>
          <button onClick={laad} className="text-zinc-500 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-600"><RefreshCw size={18} className="animate-spin mx-auto" /></div>
        ) : codes.length === 0 ? (
          <div className="text-center py-12">
            <Tag size={32} className="text-zinc-800 mx-auto mb-3" />
            <p className="text-zinc-500">Nog geen kortingscodes</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {codes.map(c => {
              const verlopen = isVerlopen(c.vervaldatum);
              const uitgeput = c.max_gebruik && c.huidig_gebruik >= c.max_gebruik;
              const effectiefActief = c.actief && !verlopen && !uitgeput;

              return (
                <div key={c.id} data-testid={`kortingscode-rij-${c.id}`}
                  className="px-6 py-4 flex items-center gap-4 hover:bg-white/2 transition-colors">
                  {/* Code badge */}
                  <div className={`px-3 py-1.5 rounded-lg font-mono font-black text-sm shrink-0 ${
                    effectiefActief
                      ? "bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30"
                      : "bg-zinc-800/50 text-zinc-500 border border-white/8 line-through"
                  }`}>
                    {c.code}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-white font-bold text-sm">{c.percentage}% korting</span>
                      {verlopen && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/25 font-bold">Verlopen</span>}
                      {uitgeput && !verlopen && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/25 font-bold">Uitgeput</span>}
                      {!c.actief && !verlopen && !uitgeput && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700/30 text-zinc-500 border border-zinc-600/25 font-bold">Gedeactiveerd</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-zinc-600 text-xs">
                      <span className="flex items-center gap-1">
                        <Hash size={10} />
                        {c.huidig_gebruik}{c.max_gebruik ? `/${c.max_gebruik}` : ""} gebruikt
                      </span>
                      {c.vervaldatum && (
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          Geldig t/m {new Date(c.vervaldatum).toLocaleDateString("nl-NL")}
                        </span>
                      )}
                      {!c.vervaldatum && <span>Geen vervaldatum</span>}
                    </div>
                  </div>

                  {/* Acties */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggle(c.id, c.actief)}
                      data-testid={`toggle-kortingscode-${c.id}`}
                      title={c.actief ? "Deactiveren" : "Activeren"}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      {c.actief
                        ? <ToggleRight size={22} className="text-[#C9A84C]" />
                        : <ToggleLeft size={22} />}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(c)}
                      data-testid={`verwijder-kortingscode-${c.id}`}
                      className="text-zinc-600 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Verwijder bevestiging */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg mb-2">Code verwijderen</h3>
            <p className="text-zinc-400 text-sm mb-6">
              Weet je zeker dat je <span className="font-mono font-black text-[#C9A84C]">{deleteConfirm.code}</span> wilt verwijderen?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
              <button onClick={() => handleVerwijder(deleteConfirm.id)} className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-xl">Verwijderen</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
